// ============================================================================
// SUGESTÃO DE TYPO DE DOMÍNIO NO EMAIL (01/08/2026)
//
// Por que existe: o /reconcile cata toda semana gente que pagou, digitou o
// email errado no cadastro e caiu numa tela sem produto. Levantamento no banco
// em 01/08: 30 domínios com typo, ~40 contas. Casos reais que custaram acesso:
//   · sfmsalgueiro@gmail.co     (faltou o m)      -> conta duplicada 3 dias depois
//   · roxannecadtleberry@yahoo  (typo no NOME)    -> não pegável aqui, ver nota
//   · lmarn@aol.vom             (v no lugar do c)
//   · gmail.con / gmail.cm / gmail.om / gmail.comj / gmail.comom / gmsil / gnail
// A avó de 68 anos digitando no celular é o caso normal, não a exceção.
//
// ESCOPO HONESTO: isto pega SÓ o domínio. Metade dos casos do reconcile é typo
// no local-part ("incorrigan" por "idacorrigan"), que nenhum validador acha
// sem saber o nome verdadeiro da pessoa.
//
// NUNCA BLOQUEIA. Sugere e deixa seguir. Bloquear um email legítimo por um
// palpite de distância de edição custa uma venda, e a lição do
// safety_net_log_only vale aqui também: rede de segurança sobre sinal falível
// não pode ter poder de veto.
// ============================================================================

// Domínios reais e frequentes na base. Alvo das sugestões.
const COMMON_DOMAINS = [
  'gmail.com', 'yahoo.com', 'hotmail.com', 'aol.com', 'outlook.com', 'icloud.com',
  'comcast.net', 'sbcglobal.net', 'att.net', 'verizon.net', 'msn.com', 'live.com',
  'me.com', 'cox.net', 'charter.net', 'bellsouth.net', 'ymail.com', 'protonmail.com',
];

// Domínios REAIS que ficam perto de um grande e não podem virar sugestão.
// Todos saíram do banco: yahoo.ca (Canadá), myyahoo.com, ptd.net (ISP da
// Pensilvânia), usa.com. Sem esta lista o validador acusa cliente honesto.
const KNOWN_GOOD = new Set([
  'yahoo.ca', 'yahoo.co.uk', 'yahoo.com.au', 'yahoo.com.br', 'myyahoo.com',
  'ptd.net', 'usa.com', 'mac.com', 'gmx.com', 'aim.com', 'juno.com', 'netzero.net',
  'googlemail.com', 'hotmail.co.uk', 'hotmail.ca', 'live.ca', 'outlook.es',
  'proton.me', 'pm.me', 'zoho.com', 'mail.com', 'fastmail.com',
]);

// Damerau: troca de duas letras vizinhas custa 1, não 2. É o erro de digitação
// mais comum que existe ("gamil" por "gmail") e o Levenshtein puro deixava passar.
function editDistance(a: string, b: string): number {
  if (a === b) return 0;
  const d: number[][] = Array.from({ length: a.length + 1 }, (_, i) =>
    Array.from({ length: b.length + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0)),
  );
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
      }
    }
  }
  return d[a.length][b.length];
}

/**
 * Devolve o email corrigido quando o domínio parece um erro de digitação de um
 * domínio comum, ou null quando não há nada a sugerir.
 */
export function suggestEmailFix(email: string): string | null {
  const clean = (email || '').trim().toLowerCase();
  const at = clean.lastIndexOf('@');
  if (at < 1 || at === clean.length - 1) return null;

  const local = clean.slice(0, at);
  const domain = clean.slice(at + 1);

  if (!domain.includes('.')) return null;
  if (COMMON_DOMAINS.includes(domain) || KNOWN_GOOD.has(domain)) return null;

  // Dedo escorregou no fim: "gmail.comom", "gmail.com1", "yahoo.comb". O domínio
  // certo está inteiro no começo e sobrou lixo.
  //
  // O teto de 3 caracteres de sobra deixava passar o email colado inteiro:
  // "yahoo.comail.com" (reconcile 24/08) sobra "ail.com", 7 chars, e a distância
  // de edição também não alcança. Ela pagou $84 e caiu numa tela sem produto.
  // Para domínio longo (>= 9 chars, o grupo gmail/yahoo/hotmail/icloud) a sobra
  // é livre desde que NÃO comece com ponto — o ponto é o que separa um domínio
  // real que por acaso começa igual ("me.company.com", "att.netsuite.com") do
  // dedo que escorregou. Domínio curto continua no teto de 3: "me.com" e
  // "cox.net" são prefixo de coisa demais.
  for (const candidate of COMMON_DOMAINS) {
    if (!domain.startsWith(candidate)) continue;
    const rest = domain.slice(candidate.length);
    if (rest.length <= 3 || (candidate.length >= 9 && !rest.startsWith('.'))) {
      return `${local}@${candidate}`;
    }
  }

  // Distância 1 pega a esmagadora maioria (gmail.co, gmail.con, gnail.com) e,
  // com Damerau, também as trocas de vizinhas (gamil.com). Distância 2 só para
  // domínio longo, senão "cox.net" viraria sugestão de qualquer coisa curta.
  let best: string | null = null;
  let bestDist = Infinity;
  for (const candidate of COMMON_DOMAINS) {
    const dist = editDistance(domain, candidate);
    const limit = candidate.length >= 10 ? 2 : 1;
    if (dist <= limit && dist < bestDist) {
      bestDist = dist;
      best = candidate;
    }
  }

  return best ? `${local}@${best}` : null;
}
